import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { route, type RouteRestArgs } from './utils';
import { appRoutes, type RouteName, type RouteParamsFor } from './routes';

/**
 * Typed navigation hook for react-router.
 *
 * Returns helpers:
 * - `nav(name, params?)` — imperative typed navigation
 * - `to(name, params?)`  — returns a URL string for `<Link to={...}>`
 * - `getParams(name)`    — parse current search params into typed object
 * - `isPublic(name)`     — check if a route is public
 *
 * @example
 *   const { nav, to, getParams } = useNav();
 *
 *   // Imperative navigation
 *   nav('wallet', { pay: 'advance' });
 *
 *   // Declarative link
 *   <Link to={to('order_list')}>Orders</Link>
 *
 *   // Read current search params (typed)
 *   const { pay, invoiceIds } = getParams('wallet');
 */
export function useNav() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  /** Imperatively navigate to a typed route */
  const navigate = useCallback(
    // Mirrors `route()` — routes with all-optional params can be
    // called with no second argument (`navigate('settings_profile')`
    // works the same as `navigate('settings_profile', { tab: '…' })`).
    <T extends RouteName>(name: T, ...rest: RouteRestArgs<T>) => {
      const url = (route as (...a: unknown[]) => string)(name, ...rest);
      nav(url);
    },
    [nav],
  );

  /** Build a URL string (for `<Link to>` or external use) */
  const to = route;

  /**
   * Parse current URL search params into a typed object for the given route.
   *
   * - String params are returned as-is.
   * - Array params (like `invoiceIds`) are split on commas.
   * - Missing params return `undefined`.
   */
  const getParams = useCallback(
    <T extends RouteName>(_name: T): Partial<NonNullable<RouteParamsFor<T>>> => {
      const result: Record<string, unknown> = {};
      for (const [key, value] of searchParams.entries()) {
        if (value.includes(',')) {
          result[key] = value.split(',');
        } else {
          result[key] = value;
        }
      }
      return result as Partial<NonNullable<RouteParamsFor<T>>>;
    },
    [searchParams],
  );

  /** Check whether the current page matches the given route name(s) */
  const isPage = useCallback(
    (...names: RouteName[]): boolean => {
      return names.some(name => {
        const routePath = appRoutes[name].path.split('?')[0];
        if (routePath.includes(':')) {
          const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
          return new RegExp(`^${pattern}$`).test(pathname);
        }
        return pathname === routePath;
      });
    },
    [pathname],
  );

  /** Check whether a route is public (no auth required) */
  const isPublic = useCallback((name: RouteName): boolean => {
    return appRoutes[name].isPublic;
  }, []);

  return { navigate, to, getParams, isPage, isPublic };
}
